/**
 * @file src/config/performanceMode.js
 * @description Performance mode — one on/off preference meaning "stop the 3D so audio gets the
 * headroom". It is NOT a graphics quality level: quality (`?graphics=`) picks how well things are
 * drawn and is baked into a deployed build, this picks WHETHER they are drawn and flips live. Audio
 * is never touched by it.
 *
 * It is one flag with one value. Three things can supply that value, in this order, and they are a
 * hand-off rather than three competing truths:
 *   1. `?performance=1` on the url — how the host (plantasia.space-root) hands the preference over at
 *      boot. NOT `?perf=`, which is already the internal LOW|MID|HIGH graphics override.
 *   2. the signed-in user's `usage.performanceMode`, so the preference follows them across devices.
 *   3. localStorage, so it survives a reload for guests and for a cold boot before the account lands.
 *
 * Whoever supplies it, the value ends up in `enabled` and everything reads THAT. All three are also
 * kept in step when the person toggles: storage and the url are written immediately, the account is
 * saved fire-and-forget. So a reload, a new tab and a later visit all agree, and nothing can quietly
 * overrule a choice that was already made.
 *
 * Every orbiter in the same window reads THIS module, so a collection / room / MultiStage view
 * switches together. Other tabs re-read on `visibilitychange` — cross-origin live sync is
 * deliberately not promised.
 */
import { fetchUserSettings, saveUserSettings } from '../api/settingsApi.js';
import { configurationEpoch, isConfigurationEpochCurrent } from './configurationPack.js';

const STORAGE_KEY = 'performanceMode';

/** The flag. */
let enabled = false;
/**
 * WHO answered, which is the only other thing worth knowing about a flag:
 *
 *   null         nobody yet — the account may still speak
 *   'device'     this browser remembers it from last time
 *   'authority'  the url said so, the account said so, or the person just toggled it
 *
 * Two rules fall out of it and there are no others. An authority is never overruled by anything
 * weaker, so the account cannot undo a toggle and a second voice booting cannot re-apply the url —
 * that last one is what used to take the 3D away from someone the moment they added a card, because
 * the url never changes when you toggle, so re-reading it always resurrected the boot value. And any
 * answer at all, even a remembered one, means nothing needs to wait for the account.
 */
let answeredBy = null;
const listeners = new Set();

function readStoredPreference() {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    return raw === '1' || raw === 'true';
  } catch {
    return null;
  }
}

function writeStoredPreference(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
  } catch {
    // Storage-blocked profile: the preference still applies for this session.
  }
}

function readUrlPreference(search) {
  try {
    const params =
      search instanceof URLSearchParams
        ? search
        : new URLSearchParams(
            typeof search === 'string' ? search : (window.location?.search ?? ''),
          );
    const raw = params.get('performance');
    if (raw === null) return null;
    const value = String(raw).trim().toLowerCase();
    if (value === '1' || value === 'true') return true;
    if (value === '0' || value === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

function emit() {
  listeners.forEach((listener) => {
    try {
      listener(enabled);
    } catch {
      // One bad subscriber must not stop the others from switching.
    }
  });
}

/** Set the flag and say who said so. Notifies only on a real change. */
function answer(value, by) {
  answeredBy = by;
  const next = Boolean(value);
  if (next === enabled) return false;
  enabled = next;
  emit();
  return true;
}

/**
 * Resolve the preference from url → localStorage synchronously, so the FIRST frame is already drawn
 * in the right mode. The account is async and lands later via `hydratePerformanceMode`.
 */
export function initPerformanceMode(search) {
  // Runs once per window. Every voice builds a whole app and calls this again, and the url does not
  // change when someone toggles — so without this, adding a card re-applied the boot value.
  if (answeredBy) return enabled;
  const fromUrl = readUrlPreference(search);
  if (fromUrl !== null) {
    // The url is the host's instruction, so it also becomes this device's remembered preference —
    // otherwise a reload inside the orbiter would silently drop back to the local value.
    writeStoredPreference(fromUrl);
    answer(fromUrl, 'authority');
    return enabled;
  }
  const stored = readStoredPreference();
  if (stored !== null) answer(stored, 'device');
  return enabled;
}

/**
 * Apply the signed-in user's saved preference — it follows them across devices, so it outranks what
 * this browser happened to remember. Ignored once an authority has spoken: the url already carries
 * the account value the host folded in, and a toggle here is the person saying it themselves.
 *
 * @returns {Promise<boolean>} the preference once the account has been consulted
 */
export async function hydratePerformanceModeFromUserSettings({ signal } = {}) {
  if (answeredBy === 'authority') return enabled;
  // Whose preference this is about, settled before the request. A read in flight when somebody else
  // arrives would otherwise land afterwards and answer AS AN AUTHORITY for the person who left —
  // and the arriving account's own hydration, which is the newer question, would then be refused by
  // the latch that answer set.
  const askedFor = configurationEpoch();
  const settings = await fetchUserSettings({ signal });
  if (answeredBy === 'authority' || !isConfigurationEpochCurrent(askedFor)) return enabled;
  const value = settings?.usage?.performanceMode;
  if (typeof value !== 'boolean') return enabled;
  writeStoredPreference(value);
  answer(value, 'authority');
  return enabled;
}

export function isPerformanceModeEnabled() {
  return enabled;
}

/**
 * Toggle it. Writes localStorage immediately (so a reload keeps it) and, for a signed-in user, saves
 * `usage.performanceMode` so it follows them. The save is fire-and-forget: the scene must switch on
 * the click, not on the round trip.
 */
export function setPerformanceModeEnabled(value) {
  const next = Boolean(value);
  writeStoredPreference(next);
  const changed = answer(next, 'authority');
  void saveUserSettings({ usage: { performanceMode: next } });
  return changed;
}

export function subscribePerformanceMode(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Another tab may have toggled it. Re-read localStorage when this one comes back to the front, so a
 * background tab catches up instead of staying in the old mode until reload.
 */
export function bindPerformanceModeVisibilitySync() {
  if (typeof document === 'undefined') return () => {};
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    const stored = readStoredPreference();
    // `null` means this device has no stored preference — never read that as "off".
    if (stored === null) return;
    answer(stored, 'device');
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => document.removeEventListener('visibilitychange', onVisible);
}

/**
 * Somebody else is here now.
 *
 * The flag is per PERSON — it lives in the account's `usage.performanceMode` — but this browser
 * remembers it per DEVICE, and the `answeredBy` latch above means a preference settled for one
 * account is never re-asked inside the same page. So a sign-in that follows a sign-out kept the
 * previous person's mode and refused to let the arriving account correct it.
 *
 * What is undone is the LATCH, not the value. The scene does not flip on its own at a moment
 * nobody asked for anything: it keeps showing what it shows, marked as this device's answer, until
 * the arriving account speaks — and now it can. The stored value stays too, because a signed-out
 * visitor's preference is still theirs.
 *
 * Called from both identity edges in `src/auth/sessionAuth.js`.
 */
export function resetPerformanceModeForIdentityChange() {
  if (answeredBy === null) return;
  // 'device', not null: the url must not be re-applied by the next voice to boot (it carries the
  // PREVIOUS account's preference, handed over by the host before this person arrived), but the
  // account must be allowed to outrank what is on screen.
  answeredBy = 'device';
}

/** Test seam: forget everything this module resolved. */
export function __resetPerformanceModeForTests() {
  enabled = false;
  answeredBy = null;
  listeners.clear();
}
